import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { BackgroundType, LayoutType } from '../../types';
import {
  elements,
  getClassName,
  useTemperature,
  useWeatherTranslations
} from '../helpers';
import type { ForecastListItem, RequestResponse } from './api/request';
import { Icon } from './common/icon';

export const Detail = ({
  data
}: {
  data: RequestResponse<ForecastListItem>;
}) => {
  const {
    customLocationName,
    typeLocation,
    layout,
    currentTemperatureSize,
    currentWeatherSize,
    backgroundColor,
    backgroundType
  } = useSettings();
  const {
    city: { name },
    list: [
      {
        main: { temp },
        weather: [currentWeather]
      }
    ]
  } = data;
  const { icon, id } = currentWeather;
  const { processedTemperature } = useTemperature({ temp });
  const weatherName = useWeatherTranslations(id);
  const locationName =
    typeLocation === 'userLocation' ? name : customLocationName || name;

  const size = (() => {
    if (layout === 'currentTemperature') {
      return currentTemperatureSize;
    }

    if (layout === 'currentWeather') {
      return currentWeatherSize;
    }

    return 0;
  })();

  return (
    <Component _layout={layout}>
      <TempBlock>
        <Show when={elements.showWidgetText(layout)}>
          <Location>
            <LocationIcon
              _backgroundType={backgroundType}
              _backgroundColor={backgroundColor}
              _layout={layout}
              _size={size}
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="12"
              viewBox="0 0 10 12"
            >
              <path
                fillRule="nonzero"
                d="M9 5c0-2.265-1.757-4-4-4-2.243 0-4 1.735-4 4 0 1.16 1.32 3.094 4 5.636C7.68 8.094 9 6.16 9 5zm-4 7C1.667 8.967 0 6.634 0 5c0-2.851 2.239-5 5-5s5 2.149 5 5c0 1.634-1.667 3.967-5 7zm0-6a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
              />
            </LocationIcon>

            <LocationName
              _backgroundType={backgroundType}
              _backgroundColor={backgroundColor}
              _layout={layout}
              _size={size}
            >
              {locationName}
            </LocationName>
          </Location>
        </Show>

        <CurrentTemp
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
          _layout={layout}
          _size={size}
        >
          {processedTemperature}
        </CurrentTemp>
      </TempBlock>

      <IconBlock _layout={layout} _size={size}>
        <Icon name={icon} />
        <Show when={elements.showWidgetText(layout)}>
          <WeatherName
            _backgroundType={backgroundType}
            _backgroundColor={backgroundColor}
            _layout={layout}
            _size={size}
          >
            {weatherName}
          </WeatherName>
        </Show>
      </IconBlock>
    </Component>
  );
};

const Component = styled.div.attrs<{
  _layout: LayoutType;
}>({
  className: getClassName('detail', 'component')
})`
  ${({ _layout }) => `
    display: flex;
    justify-content: space-between;
    padding: ${
      _layout === 'currentTemperature' || _layout === 'currentWeather'
        ? '0'
        : '24px 24px 0'
    };

    ${_layout === 'currentTemperature' ? 'align-items: center;' : ''}
  `}
`;

const TempBlock = styled.div.attrs({
  className: getClassName('detail', 'tempBlock')
})`
  display: flex;
  flex-direction: column;
`;

const Location = styled.div.attrs({
  className: getClassName('detail', 'location')
})`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const LocationName = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
  _layout: LayoutType;
  _size: number;
}>({
  className: getClassName('detail', 'locationName')
})`
  ${({ _backgroundType, _backgroundColor, _layout, _size }) => `
    margin-left: 0.2em;
    font-size: ${_layout === 'currentWeather' ? Math.max(12, _size / 3) : 17}px;
    line-height: 1.39;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-word;

    ${
      _backgroundType === 'color' && _backgroundColor
        ? `color: ${getBrightInvertedColor(_backgroundColor, ['rgb(255, 255, 255)', 'rgb(29, 33, 41)'])};`
        : ''
    }
  `}
`;

const LocationIcon = styled.svg.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
  _layout: LayoutType;
  _size: number;
}>({
  className: getClassName('detail', 'locationIcon')
})`
  ${({ _backgroundType, _backgroundColor, _layout, _size }) => `
    display: flex;
    min-width: 10px;
    fill: ${
      _backgroundType === 'color' && _backgroundColor
        ? getBrightInvertedColor(_backgroundColor, [
            'rgb(255, 255, 255)',
            'rgb(29, 33, 41)'
          ])
        : '#fff'
    };

    ${
      _layout === 'currentWeather'
        ? `
          width: ${Math.max(12, _size / 3)}px;
          height: ${Math.max(12, _size / 3)}px;
        `
        : ''
    }
  `}
`;

const CurrentTemp = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
  _layout: LayoutType;
  _size: number;
}>({
  className: getClassName('detail', 'currentTemp')
})`
  ${({ _backgroundType, _backgroundColor, _layout, _size }) => `
    font-size: 40px;
    font-weight: bold;
    margin-top: 4px;
    line-height: 1.208333;

    ${
      _backgroundType === 'color' && _backgroundColor
        ? `color: ${getBrightInvertedColor(_backgroundColor, [
            'rgb(255, 255, 255)',
            'rgb(29, 33, 41)'
          ])};`
        : ''
    }
    ${
      _layout === 'currentTemperature' || _layout === 'currentWeather'
        ? `
          margin-top: 0;
          font-size: ${_size}px;
        `
        : ''
    }
  `}
`;

const IconBlock = styled.div.attrs<{
  _layout: LayoutType;
  _size: number;
}>({
  className: getClassName('detail', 'iconBlock')
})`
  ${({ _layout, _size }) => `
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: -4px;
    margin-right: -4px;

    margin-left: ${_size / 4}px;

    ${
      _layout === 'currentTemperature'
        ? `
          width: ${_size * 1.8}px;
          height: ${_size * 1.8}px;
          margin: 0 0 0 ${_size / 4}px;
        `
        : ''
    }

    ${
      _layout === 'currentWeather'
        ? `
          width: auto;
          height: auto;
          margin: 0 0 0 ${_size * 1.25}px;
        `
        : ''
    }

  & svg {
    width: 52px;
    height: 52px;
    position: relative;
    right: -12px;

    ${
      _layout === 'currentTemperature' || _layout === 'currentWeather'
        ? `
          right: 0;
          width: ${_size * 1.8}px;
          height: ${_size * 1.8}px;
        `
        : ''
    }

    ${
      _layout === 'currentWeather'
        ? `
          right: -${_size / 6}px;
          transform: scale(1.2);
        `
        : ''
    }

    ${
      _layout === 'hourlyForecast'
        ? `
          transform: scale(1.1);
        `
        : ''
    }
  `}
`;

const WeatherName = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
  _layout: LayoutType;
  _size: number;
}>({
  className: getClassName('detail', 'weatherName')
})`
  ${({ _backgroundType, _backgroundColor, _layout, _size }) => `
    font-size: 15px;
    text-align: center;
    margin-top: 4px;
    display: flex;
    justify-content: flex-end;
    line-height: 1.39;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${
      _backgroundType === 'color' && _backgroundColor
        ? getBrightInvertedColor(_backgroundColor, [
            'rgb(255, 255, 255)',
            'rgb(29, 33, 41)'
          ])
        : '#ffffff'
    };

    ${
      _layout === 'currentWeather'
        ? `
          font-size: ${Math.max(12, _size / 3)}px;
          margin-top: 1px;
        `
        : ''
    }
  `}
`;
